import type {
  GroupOperator,
  TreeConditionNode,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';

export type TreeNodeLocation = {
  node: TreeNode;
  parentId: string | null;
  index: number;
  depth: number;
};

export type TreeOp =
  | { type: 'remove'; id: string }
  | { type: 'add'; parentId: string; index: number; node: TreeNode }
  | { type: 'move'; id: string; parentId: string; index: number }
  | {
      type: 'update';
      id: string;
      patch: {
        operator?: GroupOperator;
        negated?: boolean;
        args?: TreeConditionNode['args'];
        kind?: TreeConditionNode['kind'];
      };
    };

function indexTree(root: TreeGroupNode): Map<string, TreeNodeLocation> {
  const map = new Map<string, TreeNodeLocation>();

  const walk = (
    node: TreeNode,
    parentId: string | null,
    index: number,
    depth: number
  ) => {
    map.set(node.id, { node, parentId, index, depth });
    if (isGroupNode(node)) {
      node.children.forEach((child, childIndex) => {
        walk(child, node.id, childIndex, depth + 1);
      });
    }
  };

  walk(root, null, 0, 0);
  return map;
}

function nodesEqualForUpdate(a: TreeNode, b: TreeNode): boolean {
  if (a.type !== b.type) return false;
  if (a.negated !== b.negated) return false;

  if (a.type === 'group' && b.type === 'group') {
    return a.operator === b.operator;
  }

  if (a.type === 'condition' && b.type === 'condition') {
    return (
      a.kind === b.kind && JSON.stringify(a.args) === JSON.stringify(b.args)
    );
  }

  return false;
}

function buildUpdatePatch(
  oldNode: TreeNode,
  newNode: TreeNode
): (TreeOp & { type: 'update' }) | null {
  if (nodesEqualForUpdate(oldNode, newNode)) return null;

  const patch: {
    operator?: GroupOperator;
    negated?: boolean;
    args?: TreeConditionNode['args'];
    kind?: TreeConditionNode['kind'];
  } = {};

  if (oldNode.negated !== newNode.negated) {
    patch.negated = newNode.negated;
  }

  if (oldNode.type === 'group' && newNode.type === 'group') {
    if (oldNode.operator !== newNode.operator) {
      patch.operator = newNode.operator;
    }
  }

  if (oldNode.type === 'condition' && newNode.type === 'condition') {
    if (oldNode.kind !== newNode.kind) {
      patch.kind = newNode.kind;
    }
    if (JSON.stringify(oldNode.args) !== JSON.stringify(newNode.args)) {
      patch.args = newNode.args;
    }
  }

  // Type changed (group <-> condition): treat as full replace via remove+add elsewhere.
  if (oldNode.type !== newNode.type) {
    return null;
  }

  return { type: 'update', id: newNode.id, patch };
}

/**
 * Clone a subtree from `node`, keeping only descendants whose ids are in
 * `allowedIds` (plus the root itself). Used so an "add" of a group includes
 * its new children without emitting separate add ops for them.
 */
function cloneSubtreeForAdd(node: TreeNode, allowedIds: Set<string>): TreeNode {
  if (!isGroupNode(node)) {
    return structuredClone(node);
  }

  return {
    ...node,
    children: node.children
      .filter((child) => allowedIds.has(child.id))
      .map((child) => cloneSubtreeForAdd(child, allowedIds)),
  };
}

/**
 * ID-based tree diff. Emits removes (subtree roots), moves, updates, then adds
 * (subtree roots). Descendants of removed/added roots are not emitted separately.
 */
export function diffTrees(
  oldTree: TreeGroupNode,
  newTree: TreeGroupNode
): TreeOp[] {
  const oldIndex = indexTree(oldTree);
  const newIndex = indexTree(newTree);

  const oldIds = new Set(oldIndex.keys());
  const newIds = new Set(newIndex.keys());

  const removedIds = [...oldIds].filter((id) => !newIds.has(id));
  const addedIds = [...newIds].filter((id) => !oldIds.has(id));
  const removedSet = new Set(removedIds);
  const addedSet = new Set(addedIds);

  const ops: TreeOp[] = [];

  // Remove subtree roots only (deepest first so nested removals stay valid if
  // we ever remove non-roots independently).
  const removeRoots = removedIds.filter((id) => {
    const parentId = oldIndex.get(id)?.parentId ?? null;
    return parentId === null || !removedSet.has(parentId);
  });
  removeRoots
    .sort(
      (a, b) => (oldIndex.get(b)?.depth ?? 0) - (oldIndex.get(a)?.depth ?? 0)
    )
    .forEach((id) => {
      if (id === 'root') return;
      ops.push({ type: 'remove', id });
    });

  // Moves + updates for surviving ids
  for (const id of newIds) {
    if (!oldIds.has(id)) continue;
    if (id === 'root') {
      const update = buildUpdatePatch(
        oldIndex.get(id)!.node,
        newIndex.get(id)!.node
      );
      if (update) ops.push(update);
      continue;
    }

    const oldLoc = oldIndex.get(id)!;
    const newLoc = newIndex.get(id)!;

    // Type flip: remove + add instead of update
    if (oldLoc.node.type !== newLoc.node.type) {
      ops.push({ type: 'remove', id });
      // Will be handled as add if we also put it in added — but it's in both sets.
      // Emit explicit add after remove:
      if (newLoc.parentId) {
        ops.push({
          type: 'add',
          parentId: newLoc.parentId,
          index: newLoc.index,
          node: cloneSubtreeForAdd(newLoc.node, new Set([id])),
        });
      }
      continue;
    }

    const parentChanged = oldLoc.parentId !== newLoc.parentId;
    const indexChanged = !parentChanged && oldLoc.index !== newLoc.index;

    if ((parentChanged || indexChanged) && newLoc.parentId) {
      ops.push({
        type: 'move',
        id,
        parentId: newLoc.parentId,
        index: newLoc.index,
      });
    }

    const update = buildUpdatePatch(oldLoc.node, newLoc.node);
    if (update) ops.push(update);
  }

  // Add subtree roots only
  const addRoots = addedIds.filter((id) => {
    const parentId = newIndex.get(id)?.parentId ?? null;
    return parentId === null || !addedSet.has(parentId);
  });

  addRoots
    .sort(
      (a, b) => (newIndex.get(a)?.depth ?? 0) - (newIndex.get(b)?.depth ?? 0)
    )
    .forEach((id) => {
      if (id === 'root') return;
      const loc = newIndex.get(id);
      if (!loc?.parentId) return;
      ops.push({
        type: 'add',
        parentId: loc.parentId,
        index: loc.index,
        node: cloneSubtreeForAdd(loc.node, addedSet),
      });
    });

  return ops;
}
